import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import apiService from '../services/api.service';
import { cn } from '../lib/utils';

/**
 * Encaissé via LogFit : ce que LogFit a transféré à l'organisateur, en cumul
 * et par événement. Le chiffre est la part organisateur de chaque paiement
 * réussi : exact, rien à déduire.
 *
 * C'était une page à part, atteinte par un badge de l'app bar masqué à zéro.
 * Elle vit désormais en tête de Facturation, dont les relevés donnent le même
 * argent mois par mois : le cumul et le découpage par événement étaient les
 * seules choses qu'elle ajoutait.
 */

interface EventEarnings {
  event_id: string;
  name: string;
  date_start: string | null;
  payments_count: number;
  amount_cents: number;
}

interface EarningsResponse {
  events: EventEarnings[];
  totals: { payments_count: number; amount_cents: number };
}

const EarningsSummary: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<EarningsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const euros = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }),
    [i18n.language],
  );
  const money = useCallback((cents: number) => euros.format(cents / 100), [euros]);
  const dateShort = useCallback((iso: string | null) => (
    iso ? new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
  ), [i18n.language]);

  useEffect(() => {
    (async () => {
      try {
        setData(await apiService.getMyRevenue());
      } catch (err: any) {
        setError(err.response?.data?.detail || t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const exportCSV = () => {
    if (!data) return;
    const headers = [t('earnings.event'), t('earnings.date'), t('earnings.payments'), t('earnings.amount')];
    const rows = [
      ...data.events.map((e) => [e.name, e.date_start || '', e.payments_count, (e.amount_cents / 100).toFixed(2)]),
      [t('earnings.total'), '', data.totals.payments_count, (data.totals.amount_cents / 100).toFixed(2)],
    ];
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'logfit-encaissements.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 size={22} className="animate-spin text-muted-foreground" /></div>;
  }
  if (error || !data) {
    return (
      <Alert variant="destructive"><AlertDescription>{error || t('common.error')}</AlertDescription></Alert>
    );
  }

  const th = 'pb-2 pr-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground';
  const num = 'pr-4 py-2.5 text-right tabular-nums whitespace-nowrap';

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-3 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t('earnings.totalCollected')}</p>
          <p className="mt-1 text-3xl font-bold tabular-nums">{money(data.totals.amount_cents)}</p>
          <p className="text-xs text-muted-foreground">
            {t('earnings.paymentsCount', { count: data.totals.payments_count })} · {t('earnings.stripeNote')}
          </p>
        </div>
        {data.events.length > 0 && (
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download size={16} className="mr-1" />CSV
          </Button>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t('earnings.byEvent')}</h2>
        {data.events.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('earnings.empty')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className={th}>{t('earnings.event')}</th>
                <th className={cn(th, 'text-right')}>{t('earnings.payments')}</th>
                <th className={cn(th, 'text-right')}>{t('earnings.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.event_id} className="border-b border-border">
                  <td className="py-2.5 pr-4">
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-muted-foreground">{dateShort(e.date_start)}</div>
                  </td>
                  <td className={cn(num, 'text-muted-foreground')}>{e.payments_count}</td>
                  <td className={cn(num, 'font-semibold')}>{money(e.amount_cents)}</td>
                </tr>
              ))}
              <tr className="border-b border-border border-t-2 border-t-border font-semibold">
                <td className="py-2.5 pr-4">{t('earnings.total')}</td>
                <td className={cn(num, 'text-muted-foreground')}>{data.totals.payments_count}</td>
                <td className={num}>{money(data.totals.amount_cents)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default EarningsSummary;
